import React, { useMemo } from 'react';
import { MenuItem, SalvButton, SalvLabel, SalvMenuButton, SalvTable, Size } from '@salvapp/ui-components';
import { createColumnHelper } from '@tanstack/react-table';
import { getScenarioVersionStatus, scenarioVersionStatusColorVariant } from '../../common/versioning/helper';
import { CAN_DELETE_RISK_RULE, CAN_MODIFY_RISK_RULE } from '../../auth/permissions';
import Has from '../../auth/Has';
import FormattedDateTime from '../../common/datetime/FormattedDateTime';
import { InternalApiRiskRuleVersion, InternalApiScenarioVersionStatus } from '../../api-internal';

type VersionsV2Props = {
  versions: InternalApiRiskRuleVersion[];
  onDeleteVersion: (id: number) => void;
  onEditVersion: (id: number) => void;
  onDuplicateAsDraft: (id: number) => void;
};

enum VersionAction {
  EDIT = 'edit',
  DUPLICATE = 'duplicate',
  DELETE = 'delete',
}

export const RiskRuleVersionsTable = ({
  versions,
  onEditVersion,
  onDuplicateAsDraft,
  onDeleteVersion,
}: VersionsV2Props): React.ReactElement | null => {
  const onVersionAction = (version: InternalApiRiskRuleVersion, action: VersionAction) => {
    switch (action) {
      case VersionAction.EDIT:
        return onEditVersion(version.versionId);
      case VersionAction.DUPLICATE:
        return onDuplicateAsDraft(version.versionId);
      case VersionAction.DELETE:
        return onDeleteVersion(version.versionId);
    }
  };

  const columnHelper = createColumnHelper<InternalApiRiskRuleVersion>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('versionId', {
        header: 'Version',
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: ({ getValue }) => (
          <SalvLabel variant={scenarioVersionStatusColorVariant(getValue())}>
            {getScenarioVersionStatus(getValue())}
          </SalvLabel>
        ),
      }),
      columnHelper.accessor('note', { header: 'Note' }),

      columnHelper.accessor('updatedTime', {
        header: 'Last updated',
        cell: ({ getValue }) => <FormattedDateTime date={getValue()} showTime />,
      }),
      columnHelper.accessor('updatedBy', { header: 'Modifier', cell: ({ getValue }) => getValue()?.name }),

      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        size: 0,
        cell: ({ row }) => {
          return (
            <Has anyPermissions={[CAN_MODIFY_RISK_RULE, CAN_DELETE_RISK_RULE]}>
              {(can) => {
                return can ? (
                  <SalvMenuButton
                    ariaLabel='version-edit-dropdown'
                    onAction={(action) => onVersionAction(row.original, action[0] as VersionAction)}
                    placement='bottom right'
                  >
                    <MenuItem
                      title={row.original.status === InternalApiScenarioVersionStatus.DRAFT ? 'Edit' : 'View'}
                      value={VersionAction.EDIT}
                    />
                    <MenuItem title='Duplicate as draft' value={VersionAction.DUPLICATE} />
                    {row.original.status === InternalApiScenarioVersionStatus.DRAFT && (
                      <MenuItem title='Delete' value={VersionAction.DELETE} />
                    )}
                  </SalvMenuButton>
                ) : (
                  <SalvButton
                    variant='muted'
                    size={Size.EXTRA_SMALL}
                    onClick={() => onEditVersion(row.original.versionId)}
                    label='View'
                  />
                );
              }}
            </Has>
          );
        },
      }),
    ],
    []
  );

  return (
    <SalvTable
      data={versions}
      columns={columns}
      rowClick={(row) => onEditVersion(row.original.versionId)}
      className='m-t-16'
      emptyMessage='No risk rule versions found.'
    />
  );
};

export default RiskRuleVersionsTable;
